import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCompetition } from '../contexts/CompetitionContext';
import {
  fetchCompetitionQuestions,
  submitQuestionAnswer,
  competitionGroupsPath
} from '../services/competitionApi';
import { getTeamDisplayName } from '../utils/teamDisplayName';
import {
  getQuestionTitle,
  getOptionLabel,
  formatAnswerLabel
} from '../utils/questionDisplayName';
import { PageContainer } from '../components/layout/PageContainer';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import axios from 'axios';
import toast from 'react-hot-toast';
import { HelpCircle, CheckCircle2, Lock } from 'lucide-react';

const Predictions = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { competitionSlug } = useCompetition();
  const [questions, setQuestions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [q, teamsRes] = await Promise.all([
        fetchCompetitionQuestions(competitionSlug),
        axios.get(competitionGroupsPath(competitionSlug))
      ]);
      setQuestions(q);
      setTeams(Array.isArray(teamsRes.data) ? teamsRes.data : []);
    } catch (e) {
      console.error(e);
      toast.error(language === 'en' ? 'Failed to load predictions' : 'נכשל בטעינת התחזיות');
    } finally {
      setLoading(false);
    }
  }, [competitionSlug, language]);

  useEffect(() => {
    load();
  }, [load]);

  const myAnswer = (q) =>
    q.answers?.find((a) => String(a.userId?._id || a.userId) === String(user?._id));

  const handleSave = async (q) => {
    const draft = drafts[q._id];
    setSavingId(q._id);
    try {
      let body = {};
      if (q.type === 'team') {
        body = { teamGroupId: draft?.teamGroupId };
      } else if (q.type === 'text') {
        body = { text: draft?.text };
      } else {
        body = { optionId: draft?.optionId };
      }
      await submitQuestionAnswer(competitionSlug, q._id, body);
      toast.success(language === 'en' ? 'Saved' : 'נשמר');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || (language === 'en' ? 'Save failed' : 'שמירה נכשלה'));
    } finally {
      setSavingId(null);
    }
  };

  const statusLabel = (q) => {
    if (q.status === 'resolved') {
      return language === 'en' ? 'Resolved' : 'נפתר';
    }
    if (q.isOpen) {
      return language === 'en' ? 'Open' : 'פתוח';
    }
    return language === 'en' ? 'Closed' : 'סגור';
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex min-h-[40vh] items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">
          {language === 'en' ? 'Pre-tournament predictions' : 'תחזיות לפני הטורניר'}
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          {language === 'en'
            ? 'Answer before the competition starts. Correct picks earn bonus points on the leaderboard.'
            : 'ענה לפני תחילת התחרות. תשובות נכונות מוסיפות נקודות בטבלה.'}
        </p>
      </div>

      {questions.length === 0 ? (
        <Card className="p-8 text-center text-slate-500">
          {language === 'en' ? 'No prediction questions yet.' : 'אין שאלות תחזית עדיין.'}
        </Card>
      ) : (
        <div className="space-y-6">
          {questions.map((q) => {
            const mine = myAnswer(q);
            const open = q.isOpen;
            return (
              <Card key={q._id} className="p-5">
                <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                  <div className="flex items-start gap-2">
                    <HelpCircle className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">
                        {getQuestionTitle(q, language)}
                      </h2>
                      <p className="text-xs text-slate-500">
                        {statusLabel(q)} · {q.points}{' '}
                        {language === 'en' ? 'pts if correct' : 'נק׳ אם נכון'}
                      </p>
                    </div>
                  </div>
                  {q.status === 'resolved' ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  ) : !open ? (
                    <Lock className="h-5 w-5 text-amber-600" />
                  ) : null}
                </div>

                {open && (
                  <div className="mb-4 rounded-xl border border-brand-100 bg-brand-50/50 p-4">
                    {q.type === 'team' && (
                      <select
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        value={
                          drafts[q._id]?.teamGroupId ??
                          mine?.answer?.teamGroupId?._id ??
                          mine?.answer?.teamGroupId ??
                          ''
                        }
                        onChange={(e) =>
                          setDrafts((d) => ({
                            ...d,
                            [q._id]: { ...d[q._id], teamGroupId: e.target.value }
                          }))
                        }
                      >
                        <option value="">
                          {language === 'en' ? 'Select team' : 'בחר נבחרת'}
                        </option>
                        {teams.map((team) => (
                          <option key={team._id} value={team._id}>
                            {getTeamDisplayName(team, language)}
                          </option>
                        ))}
                      </select>
                    )}
                    {q.type === 'text' && (
                      <input
                        type="text"
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        placeholder={language === 'en' ? 'Your answer' : 'התשובה שלך'}
                        defaultValue={mine?.answer?.text || ''}
                        onChange={(e) =>
                          setDrafts((d) => ({
                            ...d,
                            [q._id]: { ...d[q._id], text: e.target.value }
                          }))
                        }
                      />
                    )}
                    {q.type === 'options' &&
                      q.options?.map((opt) => (
                        <label
                          key={opt.optionId}
                          className="mb-2 flex cursor-pointer items-center gap-2 text-sm"
                        >
                          <input
                            type="radio"
                            name={`q-${q._id}`}
                            value={opt.optionId}
                            defaultChecked={mine?.answer?.optionId === opt.optionId}
                            onChange={() =>
                              setDrafts((d) => ({
                                ...d,
                                [q._id]: { ...d[q._id], optionId: opt.optionId }
                              }))
                            }
                          />
                          {getOptionLabel(opt, language)}
                        </label>
                      ))}
                    <Button
                      className="mt-3"
                      size="sm"
                      disabled={savingId === q._id}
                      onClick={() => handleSave(q)}
                    >
                      {savingId === q._id
                        ? language === 'en'
                          ? 'Saving…'
                          : 'שומר…'
                        : language === 'en'
                          ? 'Save pick'
                          : 'שמור בחירה'}
                    </Button>
                  </div>
                )}

                {!open && mine && (
                  <p className="mb-3 text-sm font-medium text-brand-800">
                    {language === 'en' ? 'Your pick:' : 'הבחירה שלך:'}{' '}
                    {formatAnswerLabel(mine, q, language, getTeamDisplayName)}
                  </p>
                )}

                <div>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {language === 'en' ? "Everyone's picks" : 'בחירות של כולם'}
                  </h3>
                  <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                    {(q.answers || []).length === 0 ? (
                      <li className="px-3 py-2 text-sm text-slate-400">
                        {language === 'en' ? 'No picks yet' : 'אין בחירות עדיין'}
                      </li>
                    ) : (
                      q.answers.map((a) => (
                        <li
                          key={a._id}
                          className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                        >
                          <span className="font-medium text-slate-800">
                            {a.userId?.fullName || a.userId?.username || '—'}
                          </span>
                          <span className="text-slate-600">
                            {formatAnswerLabel(a, q, language, getTeamDisplayName)}
                            {q.status === 'resolved' && a.isCorrect && (
                              <span className="ms-2 text-emerald-600">
                                +{a.pointsAwarded}
                              </span>
                            )}
                          </span>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </PageContainer>
  );
};

export default Predictions;
